import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  breakdownBarRecipe,
  breakdownItemRecipe,
  breakdownListRecipe,
  breakdownRowRecipe,
  type BreakdownBarRecipeProps,
} from '../recipes/breakdown.recipe';
import { Inline, Stack } from './Layout';
import { Text } from './Text';

export interface BreakdownListProps
  extends React.HTMLAttributes<HTMLUListElement> {}

/**
 * A ranked list with a bar behind every row.
 *
 *   <BreakdownList>
 *     <BreakdownItem label="Services" value="1,204" share="48%" bar={100} />
 *     <BreakdownItem label="Phoenix" sublabel="AZ, US" value="612" share="24%" bar={51} />
 *   </BreakdownList>
 *
 * A `ul`, so a screen reader hears it as a list of so-many items. Pass `id`
 * when a "Show all" button needs something for `aria-controls`.
 */
export const BreakdownList = React.forwardRef<
  HTMLUListElement,
  BreakdownListProps
>(function BreakdownList({ className, ...props }, ref) {
  return (
    <ul ref={ref} className={cn(breakdownListRecipe(), className)} {...props} />
  );
});

export interface BreakdownItemProps
  extends Omit<React.LiHTMLAttributes<HTMLLIElement>, 'value'>,
    BreakdownBarRecipeProps {
  label: React.ReactNode;
  /** A quieter second line: the region and country under a city. */
  sublabel?: React.ReactNode;
  /** The figure, already formatted. */
  value: React.ReactNode;
  /** The figure as a share of the whole — "24%". */
  share?: React.ReactNode;
  /**
   * How wide the bar is, 0–100. The caller decides the scale: against the
   * biggest row rather than the total, usually, so a long tail does not turn
   * every bar but the first into a sliver.
   */
  bar: number;
}

export const BreakdownItem = React.forwardRef<
  HTMLLIElement,
  BreakdownItemProps
>(function BreakdownItem(
  { label, sublabel, value, share, bar, tone, className, ...props },
  ref
) {
  const width = `${Math.max(0, Math.min(100, bar))}%`;
  return (
    <li ref={ref} className={cn(breakdownItemRecipe(), className)} {...props}>
      <span
        aria-hidden="true"
        className={breakdownBarRecipe({ tone })}
        style={{ width }}
      />
      <span className={breakdownRowRecipe()}>
        <Stack as="span" gap={0} grow>
          <Text variant="body-sm" as="span" truncate>
            {label}
          </Text>
          {sublabel ? (
            <Text variant="caption" tone="muted" as="span" truncate>
              {sublabel}
            </Text>
          ) : null}
        </Stack>
        <Inline as="span" gap={3} align="baseline" className="shrink-0">
          <Text variant="body-sm-strong" as="span" numeric>
            {value}
          </Text>
          {share !== undefined ? (
            <Text
              variant="caption"
              tone="muted"
              as="span"
              numeric
              align="right"
              className="w-9"
            >
              {share}
            </Text>
          ) : null}
        </Inline>
      </span>
    </li>
  );
});
